/**
 * Payment runs (D49 phase 4): pick what is due by a date, review it by supplier, record the
 * payments in one go. Everything here rides on lib/apiClient.ts, and every figure is the server's;
 * nothing on the page re-adds what the server has already added.
 *
 * Server refusals arrive as an Error carrying `code` (machine-readable). The words a person reads
 * are a function of the CODE, written once in `paymentRunMessage` below, never the server's own
 * sentence, so a reworded server message cannot change what a screen says.
 */
import { get, postJson, put } from './apiClient';
import { PAYMENT_DATE_TOO_LATE_MESSAGE } from '../utils/paymentOnReceipt';

export type RunStatus = 'draft' | 'partial' | 'completed' | 'cancelled';
export type GroupStatus = 'pending' | 'recorded' | 'failed';

/** Money per currency, never added across currencies. */
export interface RunTotals {
  currency: string;
  amount: number;
  recorded: number;
  remaining: number;
}

export interface RunLine {
  accountsPayableId: string;
  invoiceNumber: string;
  /** A calendar day, `yyyy-mm-dd`. */
  dueDate: string | null;
  /** What is to be paid against this bill. */
  amount: number;
  /** What the bill owed when the run was saved. */
  outstandingAtSave: number;
  /**
   * What the bill owes NOW, for a line still to be recorded (null once its supplier is recorded).
   * `changed` is true when the bill now owes less than the line pays.
   */
  current: { outstanding: number | null; status: string | null; held: boolean; changed: boolean } | null;
}

/** One supplier's payment in one currency: recorded as ONE voucher, all-or-nothing. */
export interface RunGroup {
  id: string;
  /** What a reference edit names: the supplier and the currency. */
  key: string;
  supplierName: string;
  currency: string;
  reference: string;
  amount: number;
  status: GroupStatus;
  paymentId: string | null;
  /** Whether the recorded payment is still live; `void` once it was voided. */
  paymentStatus: 'applied' | 'void' | null;
  recordedAt: string | null;
  /** Why the last attempt failed, as a code (never a sentence). */
  errorCode: string;
  failedAt: string | null;
  lines: RunLine[];
}

export interface PaymentRun {
  id: string;
  runNumber: string;
  status: RunStatus;
  /** The day the selection was "due by", `yyyy-mm-dd`. */
  dueBy: string | null;
  /** The day the payments are dated, `yyyy-mm-dd`; null until chosen. */
  paymentDate: string | null;
  method: string;
  note: string;
  createdBy: string;
  createdAt: string | null;
  updatedAt: string | null;
  recordedBy: string;
  recordedAt: string | null;
  cancelledBy: string;
  cancelledAt: string | null;
  /** Someone is recording it right now. */
  recording: boolean;
  /** A draft, or a partly recorded run: what is not recorded can still change. */
  editable: boolean;
  totals: RunTotals[];
  groups: RunGroup[];
}

export interface RunListItem {
  id: string;
  runNumber: string;
  status: RunStatus;
  dueBy: string | null;
  paymentDate: string | null;
  method: string;
  createdBy: string;
  createdAt: string | null;
  recordedAt: string | null;
  supplierCount: number;
  recordedCount: number;
  failedCount: number;
  totals: RunTotals[];
}

export interface RunListPage {
  items: RunListItem[];
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface CandidateBill {
  accountsPayableId: string;
  invoiceNumber: string;
  invoiceDate: string | null;
  dueDate: string | null;
  totalAmount: number;
  outstanding: number;
  status: string;
  /** On hold or disputed: listed, never pre-selected. */
  held: boolean;
  /** The numbers of the OTHER open runs this bill is already in. */
  inRuns: string[];
}

export interface CandidateSupplier {
  key: string;
  supplierName: string;
  currency: string;
  bills: CandidateBill[];
  /** What can be paid (held bills are not in it). */
  readyAmount: number;
  heldAmount: number;
}

export interface Candidates {
  dueBy: string;
  suppliers: CandidateSupplier[];
  billCount: number;
}

export interface RecordResult {
  groupId: string;
  status: 'recorded' | 'failed';
  paymentId: string | null;
  errorCode: string;
}

export interface RecordOutcome {
  run: PaymentRun;
  results: RecordResult[];
}

export interface CandidateQuery {
  dueBy: string;
  currency?: string;
  supplier?: string;
  includeUndated?: boolean;
  /** The run being edited, so its own bills are not flagged "already in another run". */
  excludeRun?: string;
}

export interface NewRunBody {
  dueBy: string;
  currency?: string;
  supplier?: string;
}

/** What the editor saves: any of the header fields, and the COMPLETE list of bills to pay. */
export interface UpdateRunBody {
  dueBy?: string;
  paymentDate?: string | null;
  method?: string;
  note?: string;
  lines?: Array<{ accountsPayableId: string; amount: number }>;
  groups?: Array<{ key: string; reference: string }>;
}

export async function getPaymentRunCandidates(query: CandidateQuery): Promise<Candidates> {
  const qs = new URLSearchParams();
  qs.set('dueBy', query.dueBy);
  if (query.currency) qs.set('currency', query.currency);
  if (query.supplier) qs.set('supplier', query.supplier);
  if (query.includeUndated) qs.set('includeUndated', 'true');
  if (query.excludeRun) qs.set('excludeRun', query.excludeRun);
  const res = await get<{ success: boolean; data: Candidates }>(`/payment-runs/candidates?${qs.toString()}`);
  return res.data;
}

export async function listPaymentRuns(params: { status?: RunStatus | 'all'; page?: number; limit?: number } = {}): Promise<RunListPage> {
  const qs = new URLSearchParams();
  if (params.status && params.status !== 'all') qs.set('status', params.status);
  qs.set('page', String(params.page ?? 1));
  qs.set('limit', String(params.limit ?? 25));
  const res = await get<{ success: boolean; data: RunListPage }>(`/payment-runs?${qs.toString()}`);
  return res.data;
}

export async function getPaymentRun(id: string): Promise<PaymentRun> {
  const res = await get<{ success: boolean; data: PaymentRun }>(`/payment-runs/${encodeURIComponent(id)}`);
  return res.data;
}

/** Save a new draft: every open bill due by the day, at what it owes, apart from bills on hold. */
export async function createPaymentRun(body: NewRunBody): Promise<PaymentRun> {
  const res = await postJson<{ success: boolean; data: PaymentRun }>('/payment-runs', body);
  return res.data;
}

export async function updatePaymentRun(id: string, body: UpdateRunBody): Promise<PaymentRun> {
  const res = await put<{ success: boolean; data: PaymentRun }>(`/payment-runs/${encodeURIComponent(id)}`, body);
  return res.data;
}

export async function cancelPaymentRun(id: string): Promise<PaymentRun> {
  const res = await postJson<{ success: boolean; data: PaymentRun }>(`/payment-runs/${encodeURIComponent(id)}/cancel`, {});
  return res.data;
}

/** Record the payments of every supplier not yet recorded, each in its own transaction. */
export async function recordPaymentRun(id: string): Promise<RecordOutcome> {
  const res = await postJson<{ success: boolean; data: RecordOutcome }>(`/payment-runs/${encodeURIComponent(id)}/record`, {});
  return res.data;
}

// ─── Copy, a function of the code ────────────────────────────────────────────────

const MESSAGES: Record<string, string> = {
  PAYMENT_RUN_DUE_BY_REQUIRED: 'Choose the date the bills are due by.',
  PAYMENT_RUN_DUE_BY_INVALID: 'That "due by" date is not a real date. Pick it from the calendar again.',
  PAYMENT_RUN_NOTHING_DUE: 'No bills are due by that date, apart from any on hold. Choose a later date.',
  PAYMENT_RUN_TOO_MANY_BILLS: 'Too many bills are due by that date for one run. Choose an earlier date, one supplier or one currency.',
  AP_CURRENCY_INVALID: 'A currency is its three-letter code, such as TTD or USD.',
  PAYMENT_DATE_IN_FUTURE: PAYMENT_DATE_TOO_LATE_MESSAGE,
  PAYMENT_DATE_INVALID: 'That payment date is not a real date. Pick it from the calendar again.',
  PAYMENT_RUN_DATE_REQUIRED: 'Choose the date the payments are made.',
  PAYMENT_METHOD_INVALID: 'Choose how the suppliers are paid from the list.',
  PAYMENT_METHOD_REQUIRED: 'Choose how the suppliers are paid before recording.',
  PAYMENT_RUN_NOTE_TOO_LONG: 'The note is too long. Shorten it.',
  PAYMENT_RUN_NOT_FOUND: 'This payment run could not be found.',
  PAYMENT_RUN_NOT_EDITABLE: 'This payment run can no longer be changed.',
  PAYMENT_RUN_BEING_RECORDED: 'This payment run is being recorded right now. Wait a moment, then reload it.',
  PAYMENT_RUN_CHANGED: 'Someone else changed this payment run. Reload it, then make your change again.',
  PAYMENT_RUN_ALREADY_RECORDED: 'This payment run has already been recorded. Nothing was paid twice.',
  PAYMENT_RUN_CANCELLED: 'This payment run was cancelled.',
  PAYMENT_RUN_HAS_PAYMENTS: 'This payment run has payments recorded, so it cannot be cancelled. Void those payments instead.',
  PAYMENT_RUN_EMPTY: 'There is nothing left in this run to record.',
  PAYMENT_RUN_LINES_INVALID: 'The bills in this run could not be read. Reload the page and try again.',
  PAYMENT_RUN_LINE_INVALID: 'A bill in this run is not a valid bill. Reload the page and try again.',
  PAYMENT_RUN_DUPLICATE_BILL: 'A bill is in this run twice. Reload the page and try again.',
  PAYMENT_RUN_LINE_AMOUNT_INVALID: 'Every ticked bill needs an amount greater than zero.',
  PAYMENT_RUN_LINE_EXCEEDS_OUTSTANDING: 'A bill is being paid more than it owes now. It may have been paid or credited since. Reload and check the amounts.',
  PAYMENT_RUN_BILL_NOT_OWING: 'A ticked bill owes nothing now. It may have been paid or credited since. Reload and untick it.',
  PAYMENT_RUN_BILL_ALREADY_RECORDED: 'A bill was already paid by a supplier recorded in this run. Untick it.',
  PAYMENT_RUN_DUPLICATE_REFERENCE: 'Two payments to one supplier have the same reference. Change one.',
  PAYMENT_RUN_REFERENCE_TOO_LONG: 'A payment reference is too long. Shorten it.',
  PAYMENT_RUN_GROUP_UNKNOWN: 'A reference was for a supplier that is no longer in this run. Reload the page.',
  PAYABLE_NOT_FOUND: 'A bill in this run no longer exists. Reload the page and untick it.',
  // What can stop ONE supplier's payment (a group's `errorCode`); nothing was changed for that supplier.
  ALLOCATION_EXCEEDS_OUTSTANDING: 'A bill now owes less than this payment pays. It may have been paid or credited since.',
  ALLOCATION_CURRENCY_MISMATCH: "A bill is in a different currency from this payment.",
  PAYMENT_ALREADY_RECORDED: 'A payment with this reference and amount is already recorded for this supplier. Change the reference.',
  PAYMENT_WRITE_CONFLICT: 'A bill changed at the same moment. Try recording again.',
  PAYMENT_RUN_GROUP_ALREADY_RECORDED: 'This supplier was already recorded.',
  PAYMENT_RUN_GROUP_FAILED: 'This payment could not be recorded, for a reason the system could not name.',
};

/** The words for a code. An unrecognised one gets the general line, never the server's sentence. */
export function paymentRunMessage(code: string | undefined | null): string {
  if (code && Object.prototype.hasOwnProperty.call(MESSAGES, code)) return MESSAGES[code];
  return 'That could not be done. Nothing was changed. Try again.';
}

/** For a refusal thrown by apiClient. A 403 says so; everything else is by code. */
export function paymentRunRefusalMessage(err: unknown): string {
  const { code, status } = (err || {}) as { code?: string; status?: number };
  if (!code && status === 403) return 'You do not have permission to do this.';
  return paymentRunMessage(code);
}
